import * as React from 'react'
import { Button, Loader } from '@/ui'
import { ProgressBar } from '@/modules/imports/ui/ProgressBar'
import { ProgressBarRange } from '@/modules/imports/ui/ProgressBarRange'
import { useAnimatedProgress } from '../../ui/useAnimatedProgress'

type BaseProps = {
  title: string
  description?: string
  progressLabel?: string
  onAbort?: () => void
  abortLabel?: string
}

type SingleProgressProps = BaseProps & {
  progress: number // 0..1
  durationMs?: number // optional smoothing duration for transition
  easing?: string
  fromProgress?: never
  toProgress?: never
}

type RangeProgressProps = BaseProps & {
  fromProgress: number // 0..1
  toProgress: number // 0..1
  durationMs?: number
  easing?: string
  progress?: never
}

type Props = SingleProgressProps | RangeProgressProps

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
const formatPct = (v01: number) => `${Math.round(clamp01(v01) * 100)}%`

export const StepCommonPending = (props: Props) => {
  const { title, description, progressLabel, progress, onAbort, abortLabel = 'Przerwij' } = props

  const label =
    progressLabel ??
    ('progress' in props
      ? formatPct(props.progress)
      : formatPct(props.toProgress))

  const animated = useAnimatedProgress(props.progress, {
    durationMs: props.durationMs || 200,
  })

  return (
    <div className="tw-px-2 tw-py-5">
      <Loader textPosition="top">
        <div className="tw-flex tw-w-full tw-max-w-md tw-flex-col tw-items-center tw-gap-4 tw-text-center">
          <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">
            {title}
          </h3>

          {description && (
            <p className="tw-text-sm tw-leading-relaxed tw-text-gray-600">
              {description}
            </p>
          )}

          <div className="tw-mt-1 tw-w-full">
            {'progress' in props ? (
              <>
                <ProgressBar
                  progress={props.progress}
                  durationMs={props.durationMs ?? 250}
                  easing={props.easing ?? 'linear'}
                />
                <div className="tw-mt-1 tw-text-xs tw-text-gray-500">
                  {formatPct(animated)}
                </div>
              </>
            ) : (
              <ProgressBarRange
                from={props.fromProgress}
                to={props.toProgress}
                durationMs={props.durationMs ?? 2000}
                easing={props.easing ?? 'linear'}
              />
            )}
          </div>

          {onAbort && (
            <Button
              variant="error"
              // mode="outline"
              size="sm"
              text={abortLabel}
              onClick={onAbort}
              className="tw-mt-2"
            />
          )}
        </div>
      </Loader>
    </div>
  )
}
